import { v4 } from "uuid";

import type {
  CartTargetingNodeType,
  ComponentPlacementLocation,
  OnsiteUpsellProductSelection,
  OnsiteUpsellRule,
  OnsiteUpsellRuleAuthoring,
  ProductTargetingExpressionNodeType,
  RuleTargeting,
  UpsellCondition,
  UpsellProductRulesNodeType,
} from "@intelligems/ig-types";
import { UpsellNumericOperatorList } from "@intelligems/ig-types";
import {
  expressionListToPostFix,
  postfixToExpressionList,
  ShopifyId,
} from "@intelligems/ig-utils";

// The AI/panel rules (flat `{ products, conditions }`) <-> the canonical
// action/targeting expression nodes the plugin uses. RPN assembly and
// group/operator conventions are delegated to the shared expression converters,
// so this file only maps individual conditions to query nodes and back.

type CartQuery = NonNullable<CartTargetingNodeType["query"]>;
type ProductQuery = NonNullable<ProductTargetingExpressionNodeType["query"]>;
type ActionQuery = NonNullable<UpsellProductRulesNodeType["query"]>;
type NumericOperator = (typeof UpsellNumericOperatorList)[number];

// The operator values ARE the persisted numeric filter enums, so mapping is a
// pass-through; on the way back we just narrow off the include/exclude filters.
const NUMERIC_OPERATORS = new Set<string>(UpsellNumericOperatorList);

const asNumericOperator = (filter: string | undefined): NumericOperator =>
  filter && NUMERIC_OPERATORS.has(filter)
    ? (filter as NumericOperator)
    : "equalTo";

const incFilter = (include: boolean): "includes" | "doesNotInclude" =>
  include ? "includes" : "doesNotInclude";

const isExcluded = (filter?: string): boolean => filter === "doesNotInclude";

// A between condition encodes its bounds as the persisted `"min,max"` string.
const numericValue = (
  operator: NumericOperator,
  value: number,
  max?: number
): string | number =>
  operator === "between" ? `${value},${max ?? value}` : value;

// --- flat -> canonical -------------------------------------------------------

const buildAction = (
  products: OnsiteUpsellProductSelection
): UpsellProductRulesNodeType[] => {
  switch (products.source) {
    case "recommendations":
      return [
        {
          query: {
            key: "shopifyRecommendations",
            type: "shopifyRecommendations",
          },
        },
      ];
    case "collection":
      return [
        {
          query: {
            key: "collection",
            type: "collection",
            filter: incFilter(products.include),
            value: products.collectionId,
          },
        },
      ];
    case "tag":
      return [
        {
          query: {
            key: "tag",
            type: "tag",
            filter: incFilter(products.include),
            value: products.tag,
          },
        },
      ];
    case "products":
      // The canonical `productId` action value IS the product-item array, so the
      // panel's picked products pass through unchanged (backend resolves them).
      return [
        {
          query: {
            key: "productId",
            type: "productId",
            value: products.products,
          },
        },
      ];
  }
};

// Cart variants reuse the shared `productId` type (a productId -> variants map).
// Ids normalized to match the evaluator's `fromGqlId`-normalized cart items.
const toCartVariantMap = (
  variants: { productId: string; variantId: string }[]
): NonNullable<CartQuery["value"]> => {
  const map: Record<string, { variantId: string; inclusionType: "include" }[]> =
    {};
  for (const { productId, variantId } of variants) {
    const key = ShopifyId.fromGqlId(productId);
    (map[key] ??= []).push({
      variantId: ShopifyId.fromGqlId(variantId),
      inclusionType: "include",
    });
  }
  return map;
};

const toCartQuery = (condition: UpsellCondition): CartQuery | null => {
  switch (condition.type) {
    case "variants":
      return {
        key: "productId",
        type: "productId",
        filter: incFilter(condition.include),
        value: toCartVariantMap(condition.variants),
      };
    case "collection":
      return {
        key: "collection",
        type: "collection",
        filter: incFilter(condition.include),
        value: condition.collectionId,
      };
    case "tag":
      return {
        key: "tag",
        type: "tag",
        filter: incFilter(condition.include),
        value: condition.tag,
      };
    case "cartSubtotal":
    case "cartItemCount":
      return {
        key: condition.type,
        type: condition.type,
        filter: condition.operator,
        value: numericValue(condition.operator, condition.value, condition.max),
      };
    default:
      return null; // products / productPrice are pdp-only (guarded by refinement)
  }
};

const toProductQuery = (condition: UpsellCondition): ProductQuery | null => {
  switch (condition.type) {
    case "products":
      return {
        key: "productId",
        type: "productId",
        filter: incFilter(condition.include),
        value: condition.productIds.join(","),
      };
    case "collection":
      return {
        key: "collection",
        type: "collection",
        filter: incFilter(condition.include),
        value: condition.collectionId,
      };
    case "tag":
      return {
        key: "tag",
        type: "tag",
        filter: incFilter(condition.include),
        value: condition.tag,
      };
    case "productPrice":
      return {
        key: "price",
        type: "price",
        filter: condition.operator, // greaterThan | lessThan, both valid ProductFilters
        value: condition.value,
      };
    default:
      return null; // cartSubtotal / cartItemCount are cart-only
  }
};

const buildTargeting = (
  conditions: UpsellCondition[],
  location: ComponentPlacementLocation
): RuleTargeting => {
  if (location === "cart") {
    const queries = conditions
      .map(toCartQuery)
      .filter((q): q is CartQuery => q !== null);
    const expression = queries.length
      ? (expressionListToPostFix<CartQuery>(
          [{ type: queries[0].type, queries }],
          "cartTargeting"
        ) as CartTargetingNodeType[])
      : [];
    return { type: "cart", expression };
  }
  const queries = conditions
    .map(toProductQuery)
    .filter((q): q is ProductQuery => q !== null);
  const expression = queries.length
    ? (expressionListToPostFix<ProductQuery>(
        [{ type: queries[0].type, queries }],
        "experienceProductTargeting"
      ) as ProductTargetingExpressionNodeType[])
    : [];
  return { type: "product", expression };
};

// `priorRules` keeps rule *identity* stable across the round-trip: reuse the
// same `id` by position (it reaches the resolve endpoint and analytics) rather
// than minting a fresh `v4()` each conversion. `priority` is re-derived from
// position — the flat shape is positional, and resolve sorts by priority, so
// priorities must stay monotonic in `index` or precedence silently reorders.
export const flatRulesToCanonical = (
  rules: OnsiteUpsellRuleAuthoring[],
  location: ComponentPlacementLocation,
  priorRules: OnsiteUpsellRule[] = []
): OnsiteUpsellRule[] => {
  const prior = [...priorRules].sort((a, b) => a.priority - b.priority);
  return rules.map((rule, index) => ({
    id: prior[index]?.id ?? v4(),
    priority: index,
    action: buildAction(rule.products),
    targeting: buildTargeting(rule.conditions, location),
  }));
};

// --- canonical -> flat -------------------------------------------------------

const parseAction = (
  action: UpsellProductRulesNodeType[]
): OnsiteUpsellProductSelection => {
  const query = action.find((node) => node.query)?.query as
    | ActionQuery
    | undefined;
  switch (query?.key) {
    case "collection":
      return {
        source: "collection",
        include: !isExcluded(query.filter),
        collectionId: String(query.value ?? ""),
      };
    case "tag":
      return {
        source: "tag",
        include: !isExcluded(query.filter),
        tag: String(query.value ?? ""),
      };
    case "productId":
      // Only the panel's product-item array is a `products` selection; a
      // legacy/non-array `productId` value stays as recommendations.
      if (Array.isArray(query.value)) {
        return { source: "products", products: query.value };
      }
      return { source: "recommendations" };
    default:
      // shopifyRecommendations — the only remaining action shape.
      return { source: "recommendations" };
  }
};

const numericCondition = (
  type: "cartSubtotal" | "cartItemCount",
  filter: string | undefined,
  value: string | number | undefined
): UpsellCondition => {
  if (filter === "between") {
    const [min, max] = String(value ?? "").split(",");
    return {
      type,
      operator: "between",
      value: Number(min),
      max: Number(max),
    };
  }
  return {
    type,
    operator: asNumericOperator(filter),
    value: Number(value),
  };
};

const fromCartQuery = (query: CartQuery): UpsellCondition | null => {
  switch (query.type) {
    case "productId": {
      const map: Record<
        string,
        { variantId: string; inclusionType?: string }[]
      > = query.value && typeof query.value === "object" ? query.value : {};
      // Only included variants are "selected" on the flat surface; an existing
      // cart-targeting rule can persist `exclude` entries, and flattening those
      // as selected would silently broaden targeting on the next save.
      const variants = Object.entries(map).flatMap(([productId, rules]) =>
        rules
          .filter((rule) => rule.inclusionType !== "exclude")
          .map((rule) => ({ productId, variantId: rule.variantId }))
      );
      return {
        type: "variants",
        include: !isExcluded(query.filter),
        variants,
      };
    }
    case "collection":
      return {
        type: "collection",
        include: !isExcluded(query.filter),
        collectionId: String(query.value ?? ""),
      };
    case "tag":
      return {
        type: "tag",
        include: !isExcluded(query.filter),
        tag: String(query.value ?? ""),
      };
    case "cartSubtotal":
    case "cartItemCount":
      return numericCondition(
        query.type,
        query.filter,
        typeof query.value === "object" ? undefined : query.value
      );
    default:
      return null; // `anything`, or a panel-authored productId variant selection
  }
};

const fromProductQuery = (query: ProductQuery): UpsellCondition | null => {
  switch (query.type) {
    case "productId":
      return {
        type: "products",
        include: !isExcluded(query.filter),
        productIds: String(query.value ?? "")
          .split(",")
          .map((id) => id.trim())
          .filter(Boolean),
      };
    case "collection":
      return {
        type: "collection",
        include: !isExcluded(query.filter),
        collectionId: String(query.value ?? ""),
      };
    case "tag":
      return {
        type: "tag",
        include: !isExcluded(query.filter),
        tag: String(query.value ?? ""),
      };
    case "price":
      return {
        type: "productPrice",
        operator: query.filter === "lessThan" ? "lessThan" : "greaterThan",
        value: Number(query.value),
      };
    default:
      return null; // `inventory` isn't part of the flat surface
  }
};

const parseTargeting = (targeting: RuleTargeting): UpsellCondition[] => {
  if (targeting.type === "cart") {
    return postfixToExpressionList<CartQuery>(
      targeting.expression,
      "cartTargeting"
    )
      .flatMap((group) => group.queries)
      .map(fromCartQuery)
      .filter((c): c is UpsellCondition => c !== null);
  }
  if (targeting.type === "product") {
    return postfixToExpressionList<ProductQuery>(
      targeting.expression,
      "experienceProductTargeting"
    )
      .flatMap((group) => group.queries)
      .map(fromProductQuery)
      .filter((c): c is UpsellCondition => c !== null);
  }
  return []; // visitor / page targeting isn't part of the flat surface
};

// Read in `priority` order — the precedence the plugin resolves by.
export const canonicalRulesToFlat = (
  rules: OnsiteUpsellRule[]
): OnsiteUpsellRuleAuthoring[] =>
  [...rules]
    .sort((a, b) => a.priority - b.priority)
    .map((rule) => ({
      conditions: parseTargeting(rule.targeting),
      products: parseAction(rule.action),
    }));
